import axios from "axios";
import { renderDate } from "../utils/dateFormatter";

// Cache for company details
let companyDetailsCache = null;

export default async function generateConfirmation(bookingId, mode = "preview", outputType = "pdf") {
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const cdnUrl = process.env.REACT_APP_CDN_URL || "";
  const companyid = localStorage.getItem("companyid");
  const companyLogo = localStorage.getItem("companylogo");
  const logoNetShine = companyLogo && companyLogo.trim() ? `${cdnUrl}/${companyLogo}` : 'https://netshine-assets.blr1.digitaloceanspaces.com/company_87/logos/company_logo_87_1739613280498';
  const defaultLogoSrc = companyLogo && companyLogo.trim() ? `${cdnUrl}/${companyLogo}` : logoNetShine;

  // Default settings
  let settingsData = {
    heading: "Reservation Confirmation",
    showLogo: true,
    logo: defaultLogoSrc,
    footerText: "<p>We look forward to welcoming you!</p>",
    showFooter: true,
    showHeader: true,
    headerHeight: 120,
    showPolicies: false,
    contentHeight: 300,
    showPropertyInfo: true,
    hotelPolicies: "",
    header: { enabled: true, text: "" },
    footer: { enabled: true, text: "" },
    showHeaderSeparator: false,
    headerLineHeight: "120px",
  };

  // Fetch company details and settings concurrently
  let companyData = companyDetailsCache;
  let noSettingsFound = false;

  if (!companyData) {
    try {
      const [companyResponse, settingsResponse] = await Promise.all([
        fetch(`${apiUrl}/company/company-details/${companyid}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }),
        axios.get(`${apiUrl}/setting-hms/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyid },
        }),
      ]);

      if (!companyResponse.ok) throw new Error("Failed to fetch company details");
      companyData = await companyResponse.json();
      companyDetailsCache = companyData;
      console.log("Company API Response:", companyData);

      if (settingsResponse.data.success && settingsResponse.data.settings) {
        const resSettings = settingsResponse.data.settings.reservation_settings || {};
        const confSettings = resSettings.reservationConfirmation || {};
        settingsData.heading = confSettings.heading || settingsData.heading;
        settingsData.showLogo = confSettings.showLogo !== undefined ? confSettings.showLogo : settingsData.showLogo;
        settingsData.footerText = confSettings.footerText || settingsData.footerText;
        settingsData.showFooter = confSettings.showFooter !== undefined ? confSettings.showFooter : settingsData.showFooter;
        settingsData.showHeader = confSettings.showHeader !== undefined ? confSettings.showHeader : settingsData.showHeader;
        settingsData.headerHeight = confSettings.headerHeight || settingsData.headerHeight;
        settingsData.showPolicies = confSettings.showPolicies !== undefined ? confSettings.showPolicies : settingsData.showPolicies;
        settingsData.contentHeight = confSettings.contentHeight || settingsData.contentHeight;
        settingsData.showPropertyInfo = confSettings.showPropertyInfo !== undefined ? confSettings.showPropertyInfo : settingsData.showPropertyInfo;
        settingsData.hotelPolicies = resSettings.hotelPolicies || settingsData.hotelPolicies;

        const invSettings = settingsResponse.data.settings.invoice_settings || {};
        settingsData.header.text = invSettings.header?.text || settingsData.header.text;
        settingsData.footer.text = invSettings.footer?.text || settingsData.footer.text;
        settingsData.header.enabled = invSettings.header?.enabled !== undefined ? invSettings.header.enabled : settingsData.header.enabled;
        settingsData.footer.enabled = invSettings.footer?.enabled !== undefined ? invSettings.footer.enabled : settingsData.footer.enabled;
        settingsData.headerLineHeight = `${settingsData.headerHeight}px`;
      } else {
        noSettingsFound = true;
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      companyData = {
        company: "Company Name",
        address: "Address",
        city: "City",
        phone: "N/A",
        email: "N/A",
        country: "N/A",
        gst_no: "N/A",
      };
      companyDetailsCache = companyData;
      noSettingsFound = true;
    }
  } else {
    try {
      const settingsResponse = await axios.get(`${apiUrl}/setting-hms/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { companyid },
      });
      if (settingsResponse.data.success && settingsResponse.data.settings) {
        const resSettings = settingsResponse.data.settings.reservation_settings || {};
        const confSettings = resSettings.reservationConfirmation || {};
        settingsData.heading = confSettings.heading || settingsData.heading;
        settingsData.showLogo = confSettings.showLogo !== undefined ? confSettings.showLogo : settingsData.showLogo;
        settingsData.footerText = confSettings.footerText || settingsData.footerText;
        settingsData.showFooter = confSettings.showFooter !== undefined ? confSettings.showFooter : settingsData.showFooter;
        settingsData.showHeader = confSettings.showHeader !== undefined ? confSettings.showHeader : settingsData.showHeader;
        settingsData.headerHeight = confSettings.headerHeight || settingsData.headerHeight;
        settingsData.showPolicies = confSettings.showPolicies !== undefined ? confSettings.showPolicies : settingsData.showPolicies;
        settingsData.contentHeight = confSettings.contentHeight || settingsData.contentHeight;
        settingsData.showPropertyInfo = confSettings.showPropertyInfo !== undefined ? confSettings.showPropertyInfo : settingsData.showPropertyInfo;
        settingsData.hotelPolicies = resSettings.hotelPolicies || settingsData.hotelPolicies;

        const invSettings = settingsResponse.data.settings.invoice_settings || {};
        settingsData.header.text = invSettings.header?.text || settingsData.header.text;
        settingsData.footer.text = invSettings.footer?.text || settingsData.footer.text;
        settingsData.header.enabled = invSettings.header?.enabled !== undefined ? invSettings.header.enabled : settingsData.header.enabled;
        settingsData.footer.enabled = invSettings.footer?.enabled !== undefined ? invSettings.footer.enabled : settingsData.footer.enabled;
        settingsData.headerLineHeight = `${settingsData.headerHeight}px`;
      } else {
        noSettingsFound = true;
      }
    } catch (error) {
      console.error("Error fetching confirmation settings:", error);
      noSettingsFound = true;
    }
  }

  // If no settings are found, set dynamic default header and footer
  if (noSettingsFound) {
    settingsData.header.text = `
      <h1 style="font-size: 14px; margin: 0;">${companyData.company || "Company Name"}</h1>
      <p style="line-height: 10px; margin: 2px 0; font-size: 8px;">
        ${companyData.address || "Address"}, ${companyData.city || "City"}<br>
        Phone: ${companyData.phone || "N/A"}<br>
        Email: ${companyData.email || "N/A"}
      </p>
    `;
    settingsData.header.enabled = true;

    settingsData.footer.text = `
      <p style="font-size: 8px; margin-top: 5px; margin-bottom: 2px;"><strong>Thank You:</strong></p>
      <p style="font-size: 8px; margin: 2px 0;">We look forward to welcoming you!</p>
      <p style="font-size: 8px; color: #666; margin-top: 5px; text-align: center;">For inquiries, contact us at ${companyData.email || "info@hotelsunshine.com"}</p>
    `;
    settingsData.footer.enabled = true;
  }

  // Default confirmation data structure
  let confirmationData = {
    propertyInfo: { name: "", address: "", phone: "", email: "" },
    reservationInfo: {
      confirmationNumber: "",
      bookingDate: "",
      arrivalDate: "",
      departureDate: "",
      roomNumber: "",
      roomType: "",
      ratePlan: "",
      reservationTotal: "",
      deposit: "",
      balanceDue: "",
      specialRequests: "",
    },
    guestInfo: { firstName: "", lastName: "", email: "", telephone: "" },
    policies: { cancellation: "", checkIn: "", checkOut: "" },
  };

  // Fetch confirmation data using the same API as registration card
  try {
    const response = await axios.get(`${apiUrl}/booking/booking-card/${bookingId}`, {	
      headers: { Authorization: `Bearer ${token}` },
      params: { bookingId },
    });

    // Directly access propertyInfo, reservationInfo, and guestInfo from response.data
    const propertyInfo = response.data.propertyInfo || {};
    const reservationInfo = response.data.reservationInfo || {};
    const guestInfo = response.data.guestInfo || {};

    // Calculate balanceDue
    const total = parseFloat(reservationInfo.reservationTotal?.replace("₹", "") || 0);
    const deposit = parseFloat(reservationInfo.deposit?.replace("₹", "") || 0);
    const balanceDue = total - deposit;

    confirmationData = {
      propertyInfo: {
        name: propertyInfo.name || "",
        address: propertyInfo.address || "",
        phone: propertyInfo.phone || "",
        email: propertyInfo.email || "",
      },
      reservationInfo: {
        confirmationNumber: reservationInfo.reservationNumber || "",
        bookingDate: "", // Not available in API; leaving blank
        arrivalDate: renderDate(reservationInfo.arrivalDate) || "",
        departureDate: renderDate(reservationInfo.departureDate) || "",
        roomNumber: reservationInfo.roomNumber || "",
        roomType: reservationInfo.roomType || "",
        ratePlan: reservationInfo.ratePlan || "",
        reservationTotal: reservationInfo.reservationTotal || "",
        deposit: reservationInfo.deposit || "",
        balanceDue: `₹${balanceDue.toFixed(2)}`,
        specialRequests: guestInfo.additionalNotes || "",
      },
      guestInfo: {
        firstName: guestInfo.firstName || "",
        lastName: guestInfo.lastName || "",
        email: guestInfo.email || "",
        telephone: guestInfo.telephone || "",
      },
      policies: {
        cancellation: settingsData.hotelPolicies ? settingsData.hotelPolicies.match(/<strong>Cancellation Policy:<\/strong>(.*?)(?=<strong|$)/)?.[1]?.trim() || "" : "",
        checkIn: settingsData.hotelPolicies ? settingsData.hotelPolicies.match(/<strong>Check-In Policy:<\/strong>(.*?)(?=<strong|$)/)?.[1]?.trim() || "" : "",
        checkOut: settingsData.hotelPolicies ? settingsData.hotelPolicies.match(/<strong>Check-Out Policy:<\/strong>(.*?)(?=<strong|$)/)?.[1]?.trim() || "" : "",
      },
    };
  } catch (error) {
    console.error("Error fetching confirmation data:", error);
    throw new Error("Failed to fetch confirmation data");
  }

  const logoSrc = settingsData.showLogo ? settingsData.logo : "";

  const confirmationHTML = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${settingsData.heading}</title>
      <style>
        @page {
          size: A4;
          margin: 10mm 15mm; /* Reduced margins for tighter layout */
        }
        body {
          font-family: 'Helvetica', Arial, sans-serif;
          font-size: 12px;
          color: #333;
          line-height: 1.4;
          margin: 0;
          padding: 0;
          background-color: #fff;
        }
        .container {
          max-width: 650px;
          margin: 0 auto;
          padding: 0 10px; /* Reduced padding */
        }
        .headerp {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 5px 0; /* Reduced padding */
          margin-bottom: 5px; /* Reduced margin */
          border-bottom: 1px solid #ddd;
          width: 100%;
          box-sizing: border-box;
        }
        .headerp img {
          max-width: 120px; /* Reduced logo width */
          height: auto;
          vertical-align: top;
        }
        .header-text {
          text-align: right;
          flex-grow: 1;
          padding-left: 10px; /* Reduced padding */
        }
        .header-text h1 {
          font-size: 14px;
          margin: 0 0 2px; /* Reduced margin */
          color: #222;
        }
        .header-text p {
          margin: 0;
          line-height: 1.2;
          font-size: 10px;
          color: #555;
        }
        .title {
          text-align: center;
          font-size: 20px; /* Reduced font size */
          font-weight: 700;
          margin: 10px 0; /* Reduced margin */
          color: #1a3c34;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .content {
          padding: 10px; /* Reduced padding */
          background: #f9f9f9;
        }
        .section {
          margin-bottom: 10px; /* Reduced margin */
        }
        .section h2 {
          font-size: 14px; /* Reduced font size */
          font-weight: 600;
          color: #1a3c34;
          margin-bottom: 5px; /* Reduced margin */
          padding-bottom: 2px; /* Reduced padding */
          border-bottom: 1px solid #1a3c34;
        }
        .section p {
          margin: 3px 0; /* Reduced margin */
          display: flex;
          align-items: flex-start;
        }
        .section p strong {
          width: 150px; /* Reduced width */
          font-weight: 600;
          color: #34495e;
          flex-shrink: 0;
          text-align: right;
          margin-right: 8px; /* Reduced margin */
        }
        .section p span {
          flex: 1;
          color: #555;
        }
        .policies {
          font-size: 11px;
          color: #666;
          padding: 8px; /* Reduced padding */
          border-left: 2px solid #27ae60; /* Reduced border width */
          margin-top: 10px; /* Reduced margin */
        }
        .policies h2 {
          font-size: 14px; /* Reduced font size */
          margin-bottom: 5px; /* Reduced margin */
        }
        .signature {
          text-align: right;
          margin-top: 15px; /* Reduced margin */
          font-style: italic;
          color: #7f8c8d;
          font-size: 12px;
        }
        .footer {
          text-align: center;
          margin-top: 15px; /* Reduced margin */
          padding-top: 8px; /* Reduced padding */
          border-top: 1px solid #ddd;
          font-size: 10px;
          color: #7f8c8d;
        }
      </style>
    </head>
    <body>
      <div class="container">
        <!-- Header Section -->
        ${settingsData.showHeader ? `
          <div class="headerp">
            <div>
              ${settingsData.showLogo ? `<img src="${logoSrc}" alt="Company Logo">` : ""}
            </div>
            <div class="header-text">
              ${settingsData.header.text}
            </div>
          </div>
        ` : ""}

        <h1 class="title">${settingsData.heading}</h1>

        <div class="content">
          <!-- Property Information -->
          ${settingsData.showPropertyInfo ? `
            <div class="section">
              <h2>Property Information</h2>
              <p><strong>Name:</strong> <span>${confirmationData.propertyInfo.name}</span></p>
              <p><strong>Address:</strong> <span>${confirmationData.propertyInfo.address}</span></p>
              <p><strong>Phone Number:</strong> <span>${confirmationData.propertyInfo.phone}</span></p>
              <p><strong>E-mail:</strong> <span>${confirmationData.propertyInfo.email}</span></p>
            </div>
          ` : ""}

          <!-- Reservation Details -->
          <div class="section">
            <h2>Reservation Details</h2>
            <p><strong>Confirmation Number:</strong> <span>${confirmationData.reservationInfo.confirmationNumber}</span></p>
            <p><strong>Booking Date:</strong> <span>${confirmationData.reservationInfo.bookingDate}</span></p>
            <p><strong>Arrival Date:</strong> <span>${confirmationData.reservationInfo.arrivalDate}</span></p>
            <p><strong>Departure Date:</strong> <span>${confirmationData.reservationInfo.departureDate}</span></p>
            <p><strong>Room Number/Name:</strong> <span>${confirmationData.reservationInfo.roomNumber}</span></p>
            <p><strong>Room Type:</strong> <span>${confirmationData.reservationInfo.roomType}</span></p>
            <p><strong>Rate Plan:</strong> <span>${confirmationData.reservationInfo.ratePlan}</span></p>
            <p><strong>Special Requests:</strong> <span>${confirmationData.reservationInfo.specialRequests}</span></p>
          </div>

          <!-- Guest Information -->
          <div class="section">
            <h2>Guest Information</h2>
            <p><strong>Name:</strong> <span>${confirmationData.guestInfo.firstName}${confirmationData.guestInfo.lastName ? " " + confirmationData.guestInfo.lastName : ""}</span></p>
            <p><strong>Email:</strong> <span>${confirmationData.guestInfo.email}</span></p>
            <p><strong>Telephone:</strong> <span>${confirmationData.guestInfo.telephone}</span></p>
          </div>

          <!-- Payment Summary -->
          <div class="section">
            <h2>Payment Summary</h2>
            <p><strong>Reservation Total:</strong> <span>${confirmationData.reservationInfo.reservationTotal}</span></p>
            <p><strong>Deposit Paid:</strong> <span>${confirmationData.reservationInfo.deposit}</span></p>
            <p><strong>Balance Due:</strong> <span>${confirmationData.reservationInfo.balanceDue}</span></p>
          </div>

          <!-- Hotel Policies -->
          ${settingsData.showPolicies ? `
            <div class="section policies">
              <p>${settingsData.hotelPolicies}</p>
            </div>
          ` : ""}
        </div>

        <!-- Signature -->
        <div class="signature">
          <p>Sincerely,</p>
          <p>The Team at ${confirmationData.propertyInfo.name}</p>
        </div>

        <!-- Footer -->
        ${settingsData.showFooter ? `
          <div class="footer">
            ${settingsData.footerText}
          </div>
        ` : ""}
      </div>
    </body>
    </html>
  `;

  // Handle different modes
  try {
    if (mode === "email") {
      const response = await axios.post(`${apiUrl}/transactions/send-reg-email`, [
        {
          bookingId,
          guestName: `${confirmationData.guestInfo.firstName}${confirmationData.guestInfo.lastName ? " " + confirmationData.guestInfo.lastName : ""}`.trim(),
          guestEmail: confirmationData.guestInfo.email,
          checkInDate: confirmationData.reservationInfo.arrivalDate,
          checkOutDate: confirmationData.reservationInfo.departureDate,
          confirmationNumber: confirmationData.reservationInfo.confirmationNumber,
          html: confirmationHTML,
          emailType: "confirmation",
        },
      ], {
        headers: { Authorization: `Bearer ${token}` },
      });
      return { message: "Reservation Confirmation emailed successfully!" };
    } else if (mode === "preview" && outputType === "html") {
      return confirmationHTML; // Return HTML string for direct rendering
    } else {
      const response = await axios.post(`${apiUrl}/transactions/generate-pdf`, { html: confirmationHTML }, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "arraybuffer",
      });

      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      return pdfBlob;
    }
  } catch (error) {
    console.error("Error processing confirmation:", error);
    throw new Error("Failed to process confirmation");
  }
}